import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useState, useEffect, useRef, } from 'react';
import { useOptionalDependencies } from '@servicetitan/react-ioc';
import { Input } from '@servicetitan/design-system';
import { CULTURE_TOKEN } from '@servicetitan/culture';
import { processValue } from './process-value';
import { getFormattedString } from './get-formatted-string';
import { formatNumber as formatNumberDefault } from 'accounting';
import debounce from 'debounce';
const ARROW_UP_KEY = 38;
const ARROW_DOWN_KEY = 40;
const DEBOUNCE_WAIT = 300;
export const NumberInput = ({ value, onChange, onFocus, onBlur, emptyValue, decimalPlaces, useEmptyThousandsSeparator, min, max, useKeyboardNavigation, formatNumber = formatNumberDefault, ...props }) => {
    const [{ NumberFormat: { NumberGroupSeparator: thousandsSeparator = ',', NumberDecimalSeparator: decimalSeparator = '.', } = {}, } = {},] = useOptionalDependencies(CULTURE_TOKEN);
    const getFormattedValue = useCallback(() => getFormattedString(value, {
        emptyValue,
        precision: decimalPlaces,
        separators: {
            thousand: useEmptyThousandsSeparator ? '' : thousandsSeparator,
            decimal: decimalSeparator,
        },
    }, formatNumber), [
        decimalPlaces,
        decimalSeparator,
        emptyValue,
        thousandsSeparator,
        useEmptyThousandsSeparator,
        value,
        formatNumber,
    ]);
    const [isFocused, setIsFocused] = useState(false);
    const [state, setState] = useState({
        numberValue: value,
        stringValue: getFormattedValue(),
    });
    useEffect(() => {
        if (isFocused) {
            return;
        }
        setState({
            numberValue: value,
            stringValue: getFormattedValue(),
        });
    }, [value, getFormattedValue, isFocused]);
    const handleChange = (_0, { value }) => {
        updateValue(value);
    };
    const handleKeyDown = (event) => {
        var _a;
        if (event.keyCode === ARROW_UP_KEY || event.keyCode === ARROW_DOWN_KEY) {
            const newValue = ((_a = state.numberValue) !== null && _a !== void 0 ? _a : 0) + (event.keyCode === ARROW_UP_KEY ? 1 : -1);
            updateValue(String(newValue));
            event.preventDefault();
        }
    };
    /*
     ** "updateImmediateValue" updates the value immediately without clamping min/max,
     ** then "updateClampedValueDebounced" updates applying the min/max after debounce delay
     **
     ** Example: User can type "100" if min is "5" without the "1" immediately being clamped
     */
    const updateImmediateValue = (value, silent = false) => {
        const newState = processValue(value, {
            emptyValue,
            precision: decimalPlaces,
            separators: {
                thousand: thousandsSeparator,
                decimal: decimalSeparator,
            },
        }, formatNumber);
        setState(newState);
        if (!silent) {
            onChange(newState.numberValue);
        }
    };
    const updateClampedValueDebounced = useRef();
    useEffect(() => {
        let isCurrent = true;
        const updateClampedValue = (value, silent = false) => {
            if (isCurrent) {
                const newState = processValue(value, {
                    emptyValue,
                    precision: decimalPlaces,
                    separators: {
                        thousand: thousandsSeparator,
                        decimal: decimalSeparator,
                    },
                    range: { min, max },
                }, formatNumber);
                setState(newState);
                if (!silent) {
                    onChange(newState.numberValue);
                }
            }
        };
        updateClampedValueDebounced.current = debounce(updateClampedValue, DEBOUNCE_WAIT);
        return () => {
            isCurrent = false;
        };
    }, [
        emptyValue,
        decimalPlaces,
        thousandsSeparator,
        decimalSeparator,
        min,
        max,
        formatNumber,
        onChange,
    ]);
    const updateValue = (value, silent = false) => {
        var _a;
        updateImmediateValue(value, silent);
        (_a = updateClampedValueDebounced.current) === null || _a === void 0 ? void 0 : _a.call(updateClampedValueDebounced, value, silent);
    };
    const handleFocus = (event) => {
        if (state.stringValue.length) {
            updateValue(
            // prepare the value which user does see (rounded), but without trailing zeroes after decimal and separator
            decimalPlaces
                ? state.stringValue.replace(new RegExp(`[${decimalSeparator}]?0+$`), '')
                : state.stringValue, true);
        }
        setIsFocused(true);
        if (onFocus) {
            onFocus(event);
        }
    };
    const handleBlur = (event) => {
        setState({
            numberValue: value,
            stringValue: getFormattedValue(),
        });
        setIsFocused(false);
        if (onBlur) {
            onBlur(event);
        }
    };
    return (_jsx(Input, { ...props, value: state.stringValue, onChange: handleChange, onFocus: handleFocus, onBlur: handleBlur, onKeyDown: useKeyboardNavigation ? handleKeyDown : undefined }));
};
//# sourceMappingURL=number-input.js.map